import { useQuery, useInfiniteQuery, useMutation, useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { toastError } from '@strivelabs/ui-core';
import { listAllWorkflowExecutions, deleteWorkflowExecution, deleteWorkflowExecutions, deleteInChunks, updateWorkflowExecution, BASE_URL, BULK_DELETE_BATCH_LIMIT, type ChatAttachment, type ChatCitation } from '@/services/DataService';
import { useAppStore } from '@/store/appStore';

export interface ChatHistoryToolCall {
  id: string;
  name: string;
  arguments: string;
  stream_to_ui?: boolean;
}

export interface ChatHistoryToolCallOutput {
  id: string;
  output: string;
}

export interface ChatHistoryItem {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  origin: string;
  created_at: string;
  updated_at: string;
  behaviour_action_state: Record<string, unknown>;
  tool_calls?: ChatHistoryToolCall[];
  tool_call_outputs?: ChatHistoryToolCallOutput[];
  files?: ChatAttachment[];
  citations?: ChatCitation[];
}

export interface WorkflowExecution {
  status: 'COMPLETED' | 'IN_PROGRESS' | 'FAILED' | 'APPROVAL_PENDING';
  id: number;
  title: string | null;
  chat_mode_id?: string | null;
  created_at: string;
  updated_at: string;
  /** When the signed-in user pinned this conversation, null when they have not. Absent on a row the cache holds before the server has one. */
  pinned_at?: string | null;
  /** Who started the conversation. */
  user_id?: string;
  workflow_execution_chat_history?: ChatHistoryItem[];
}

interface WorkflowExecutionsResponse {
  workflow_executions: WorkflowExecution[];
}

/** Server-side filters every conversation list query accepts. */
export interface WorkflowExecutionListFilters {
  pinned?: boolean;
  user_id?: string;
  created_after?: string;
  created_before?: string;
  sort_by?: 'id' | 'title' | 'created_at';
  sort_order?: 'asc' | 'desc';
}

/** What one chunk of a bulk delete did. */
export interface BulkDeleteWorkflowExecutionsResult {
  deleted: string[];
  failed: { ids: string[]; error: unknown }[];
}

interface WorkflowExecutionsInfinitePage {
  executions: WorkflowExecution[];
  currentPage: number;
  hasMore: boolean;
}

interface WorkflowExecutionsInfiniteData {
  pages: WorkflowExecutionsInfinitePage[];
  pageParams: unknown[];
}

interface PinEdit {
  execution: WorkflowExecution;
  pinned: boolean;
  pinnedAt: string | null;
}

interface PinWorkflowExecutionVariables {
  id: string;
  pinned: boolean;
}

interface PinWorkflowExecutionContext {
  snapshots: [QueryKey, unknown][];
}

/** Query key roots holding a conversation list, newest cache shape last. */
const EXECUTION_LIST_KEY_ROOTS = [
  'workflowExecutions',
  'workflowExecutionsInfinite'
] as const;

/** Every cached conversation list for one instance, with its query key. */
const getExecutionListQueries = (
  queryClient: QueryClient,
  workflowInstanceId: string
): [QueryKey, unknown][] =>
  EXECUTION_LIST_KEY_ROOTS.flatMap(root =>
    queryClient.getQueriesData({ queryKey: [root, workflowInstanceId] }));

/** Re-fetches every conversation list for one instance. */
const invalidateExecutionLists = (queryClient: QueryClient, workflowInstanceId: string) => {
  EXECUTION_LIST_KEY_ROOTS.forEach(root => {
    queryClient.invalidateQueries({ queryKey: [root, workflowInstanceId] });
  });
};

/** Turns filters into request params, leaving out the ones nobody set. */
const toExecutionQueryParams = (filters: WorkflowExecutionListFilters) => ({
  ...(filters.pinned !== undefined && { pinned: filters.pinned }),
  ...(filters.user_id && { user_id: filters.user_id }),
  ...(filters.created_after && { created_after: filters.created_after }),
  ...(filters.created_before && { created_before: filters.created_before }),
  ...(filters.sort_by && { sort_by: filters.sort_by }),
  ...(filters.sort_order && { sort_order: filters.sort_order }),
});

const isExecution = (execution: WorkflowExecution, id: string) => String(execution.id) === id;

/** Rows a cached list holds, whatever shape that cache has. */
const readCachedExecutions = (queryKey: QueryKey, data: unknown): WorkflowExecution[] => {
  const [root] = queryKey as [string];

  if (root === 'workflowExecutions') {
    return (data as WorkflowExecutionsResponse).workflow_executions;
  }

  return (data as WorkflowExecutionsInfiniteData).pages.flatMap(page => page.executions);
};

const findCachedExecution = (snapshots: [QueryKey, unknown][], id: string) =>
  snapshots
    .filter(([, data]) => data !== undefined)
    .flatMap(([queryKey, data]) => readCachedExecutions(queryKey, data))
    .find(execution => isExecution(execution, id));

/** Whether a cache's sort matches the server's default (created_at/id descending) — the order `insertExecution` assumes. A cache sorted some other way is left for the refetch to place correctly rather than guessed at. */
const isDefaultSort = (filters: WorkflowExecutionListFilters): boolean =>
  (filters.sort_by === undefined || filters.sort_by === 'created_at') &&
  (filters.sort_order === undefined || filters.sort_order === 'desc');

/** Whether a row would satisfy a cached list's own search/owner/date filters, checked before inserting it — the pin edit only tells us the row's new pinned state, not whether it belongs in a list narrowed by those. */
const matchesCacheFilters = (
  execution: WorkflowExecution,
  search: string,
  filters: WorkflowExecutionListFilters
): boolean => {
  const term = search.trim().toLowerCase();
  if (term && !(execution.title ?? '').toLowerCase().includes(term)) return false;
  if (filters.user_id && execution.user_id !== filters.user_id) return false;
  if (filters.created_after && execution.created_at < filters.created_after) return false;
  if (filters.created_before && execution.created_at > filters.created_before) return false;
  return true;
};

/** Where a row re-enters a list: a pinned shelf leads with the newest pin, every other list stays created_at descending. */
const insertExecution = (
  executions: WorkflowExecution[],
  execution: WorkflowExecution,
  shelf: boolean
): WorkflowExecution[] => {
  if (shelf) return [execution, ...executions];

  const index = executions.findIndex(item => item.created_at < execution.created_at);
  if (index === -1) return [...executions, execution];

  return [...executions.slice(0, index), execution, ...executions.slice(index)];
};

/**
 * Applies a pin edit to one cached list. `shelf` is that query's `pinned`
 * filter, so a row whose new state no longer matches leaves the list, and a row
 * the list now wants joins its leading page — but only when `canInsert` says
 * the row actually belongs there (its sort is the assumed default, and it
 * satisfies the list's other filters).
 */
const applyPinEdit = (
  executions: WorkflowExecution[],
  edit: PinEdit,
  shelf: boolean | undefined,
  leadingPage: boolean,
  canInsert: boolean
): WorkflowExecution[] => {
  const id = String(edit.execution.id);
  const isPresent = executions.some(execution => isExecution(execution, id));

  if (shelf !== undefined && shelf !== edit.pinned) {
    return isPresent ? executions.filter(execution => !isExecution(execution, id)) : executions;
  }

  if (isPresent) {
    return executions.map(execution =>
      isExecution(execution, id) ? { ...execution, pinned_at: edit.pinnedAt } : execution);
  }

  if (shelf === undefined || !leadingPage || !canInsert) return executions;

  return insertExecution(executions, { ...edit.execution, pinned_at: edit.pinnedAt }, shelf);
};

/** Rewrites one cached list, reading its filters off its query key. */
const applyPinEditToCache = (queryKey: QueryKey, data: unknown, edit: PinEdit): unknown => {
  const [root, , ...rest] = queryKey as [string, string, ...unknown[]];

  if (root === 'workflowExecutions') {
    const response = data as WorkflowExecutionsResponse;
    return {
      ...response,
      workflow_executions: applyPinEdit(response.workflow_executions, edit, undefined, false, true)
    };
  }

  const [search, filters] = rest as [string, WorkflowExecutionListFilters];
  const infinite = data as WorkflowExecutionsInfiniteData;
  const canInsert = isDefaultSort(filters) && matchesCacheFilters(edit.execution, search, filters);
  return {
    ...infinite,
    pages: infinite.pages.map((page, index) => ({
      ...page,
      executions: applyPinEdit(page.executions, edit, filters.pinned, index === 0, canInsert)
    }))
  };
};

interface UseWorkflowExecutionsReturn {
  /**
   * Returns all workflow executions
   */
  executions: WorkflowExecution[] | undefined;
  
  /**
   * Returns the latest completed execution
   */
  getLatestCompletedExecution: () => WorkflowExecution | undefined;
  
  /**
   * Returns all completed executions sorted by created_at descending
   */
  getCompletedExecutions: () => WorkflowExecution[];
  
  /** Query status */
  isLoading: boolean;
  error: Error | null;
}

export const useWorkflowExecutions = (
  workflowInstanceId: string,
  enabled: boolean = false
): UseWorkflowExecutionsReturn => {
  const { data, isLoading, error } = useQuery<WorkflowExecutionsResponse>({
    queryKey: ['workflowExecutions', workflowInstanceId],
    queryFn: async () => {
      return await listAllWorkflowExecutions(BASE_URL, workflowInstanceId, {sort_by: 'id', sort_order: 'desc' });
    },
    enabled: enabled && Boolean(BASE_URL) && Boolean(workflowInstanceId),
    // Override global setting to refetch when window regains focus
    // Critical for updating UI state (e.g., showNewAnalysisButton) after user returns from idle
    refetchOnWindowFocus: true,
  });

  const executions = data?.workflow_executions;

  const getCompletedExecutions = () => {
    if (!executions) return [];

    return executions
      .filter(execution => execution.status === 'COMPLETED')
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  };

  const getLatestCompletedExecution = () => {
    const completedExecutions = getCompletedExecutions();
    return completedExecutions.length > 0 ? completedExecutions[0] : undefined;
  };

  return {
    executions,
    getLatestCompletedExecution,
    getCompletedExecutions,
    isLoading,
    error
  };
};

/**
 * Hook for infinite scroll pagination of workflow executions.
 * Optionally filters server-side by title substring — pass a debounced value
 * from the caller so the query key doesn't churn on every keystroke. `filters`
 * narrows the list further, server-side. Both, and `pageSize`, are part of the
 * query key, so a rail asking for short pages and a page asking for long ones
 * hold separate caches.
 */
export const useWorkflowExecutionsInfinite = (
  workflowInstanceId: string,
  enabled: boolean = false,
  search: string = '',
  filters: WorkflowExecutionListFilters = {},
  pageSize: number = 20
) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isRefetching,
    error
  } = useInfiniteQuery({
    queryKey: ['workflowExecutionsInfinite', workflowInstanceId, search, filters, pageSize],
    queryFn: async ({ pageParam = 1, signal }) => {
      const response = await listAllWorkflowExecutions(BASE_URL, workflowInstanceId, {
        sort_by: 'id',
        sort_order: 'desc',
        page_size: pageSize,
        page: pageParam,
        ...(search && { title: search }),
        ...toExecutionQueryParams(filters),
      }, signal);
      return {
        executions: response?.workflow_executions || [],
        currentPage: pageParam,
        hasMore: (response?.workflow_executions?.length || 0) === pageSize
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.currentPage + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: enabled && Boolean(BASE_URL) && Boolean(workflowInstanceId),
  });

  // Flatten all pages into a single array
  const executions = data?.pages.flatMap(page => page.executions) ?? [];

  return {
    executions,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isRefetching,
    error
  };
};

// Simple UI helpers for Trigger screens and similar views
export const useHasAnyWorkflowExecutions = (opts?: { useCompletedOnly?: boolean }) => {
  const activeWorkflowInstance = useAppStore(state => state.activeWorkflowInstance);
  const workflowInstanceId = activeWorkflowInstance?.id ? String(activeWorkflowInstance.id) : '';
  const { executions, isLoading, error } = useWorkflowExecutions(
    workflowInstanceId,
    Boolean(workflowInstanceId)
  );

  const hasAny = (executions?.length ?? 0) > 0;
  const hasCompleted = executions?.some(execution => execution.status === 'COMPLETED') ?? false;

  const useCompletedOnly = opts?.useCompletedOnly ?? false;
  const hasPastExecutions = useCompletedOnly ? hasCompleted : hasAny;

  return { hasPastExecutions, isLoading, error };
};

export const useDeleteWorkflowExecution = (workflowInstanceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workflowExecutionId: string) =>
      deleteWorkflowExecution(workflowInstanceId, workflowExecutionId),
    onSuccess: () => {
      invalidateExecutionLists(queryClient, workflowInstanceId);
    },
  });
};

/**
 * Delete many conversations. The server takes 50 ids per request and refuses a
 * batch whole, so the ids go out in chunks, one chunk at a time, and the result
 * says which ids went and which chunks failed instead of throwing.
 */
export const useDeleteWorkflowExecutions = (workflowInstanceId: string) => {
  const queryClient = useQueryClient();

  return useMutation<BulkDeleteWorkflowExecutionsResult, Error, string[]>({
    mutationFn: async (ids) => {
      const { deleted, failed } = await deleteInChunks(
        ids,
        BULK_DELETE_BATCH_LIMIT,
        (chunk) => deleteWorkflowExecutions(workflowInstanceId, chunk)
      );

      return { deleted, failed };
    },
    onSuccess: () => {
      invalidateExecutionLists(queryClient, workflowInstanceId);
    },
  });
};

/**
 * Pin or unpin a conversation for the signed-in user. Every cached conversation
 * list for the instance is rewritten before the request lands — the row's
 * `pinned_at` changes and the row moves between the pinned and unpinned
 * shelves — then rolled back and toasted on error, and re-fetched once settled.
 */
export const usePinWorkflowExecution = (workflowInstanceId: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    WorkflowExecution,
    Error,
    PinWorkflowExecutionVariables,
    PinWorkflowExecutionContext
  >({
    mutationFn: ({ id, pinned }) =>
      updateWorkflowExecution(workflowInstanceId, id, { pinned }),
    onMutate: async ({ id, pinned }) => {
      await Promise.all(EXECUTION_LIST_KEY_ROOTS.map(root =>
        queryClient.cancelQueries({ queryKey: [root, workflowInstanceId] })));

      const snapshots = getExecutionListQueries(queryClient, workflowInstanceId);
      const execution = findCachedExecution(snapshots, id);
      if (!execution) return { snapshots };

      const edit: PinEdit = {
        execution,
        pinned,
        pinnedAt: pinned ? new Date().toISOString() : null,
      };
      snapshots.forEach(([queryKey, data]) => {
        if (data === undefined) return;
        queryClient.setQueryData(queryKey, applyPinEditToCache(queryKey, data, edit));
      });

      return { snapshots };
    },
    onError: (_error, _variables, context) => {
      context?.snapshots.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      toastError("Couldn't update pin");
    },
    onSettled: () => {
      invalidateExecutionLists(queryClient, workflowInstanceId);
    },
  });
};

/**
 * Rename a workflow execution. `title === ''` or `null` clears the title back
 * to auto-title fallback behavior. Invalidates both the paginated sidebar
 * list and the flat list so any consumer re-fetches. The per-execution
 * chat-history cache does not carry the title, so it is not invalidated here.
 */
export const useUpdateWorkflowExecution = (workflowInstanceId: string) => {
  const queryClient = useQueryClient();

  return useMutation<WorkflowExecution, Error, { workflowExecutionId: string; title: string | null }>({
    mutationFn: ({ workflowExecutionId, title }) =>
      updateWorkflowExecution(workflowInstanceId, workflowExecutionId, { title }),
    onSuccess: () => {
      invalidateExecutionLists(queryClient, workflowInstanceId);
    },
  });
};
